import { useNavigate, useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import MainTopBar from '@/components/common/bar/MainTopBar'
import { Trash2 } from 'lucide-react'
import ReportDetailGridBox from '@/components/report/ReportDetailGridBox'
import CardOne from '@/components/common/card/CardOne'
import CommonModal from '@/components/common/modal/CommonModal'
import { useGetMyReports, useGetReportDetail } from '@/hooks/queries/report'
import { useDeleteReport } from '@/hooks/queries/report/mutation'
import ToastMessage from '@/components/common/toast/ToastMessage'
import { useUserStore } from '@/store/userStore'
import { useScrollToTop } from '@/hooks/useScrollToTop'

export default function ReportDetailPage() {
  useScrollToTop()

  const params = useParams()
  const navigate = useNavigate()
  const reportId = params.reportId as string
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showToast, setShowToast] = useState<boolean>(false)

  const { data: reportData } = useGetReportDetail(Number(reportId))
  const { mutate: deleteReport } = useDeleteReport()
  const { refetch: refetchMyReports } = useGetMyReports()
  const { userInfo } = useUserStore()

  useEffect(() => {
    if (reportData) {
      refetchMyReports()
    }
  }, [reportData])

  const handleDelete = () => {
    setShowDeleteModal(false)
    deleteReport(Number(reportId), {
      onSuccess: () => {
        navigate('/report')
      },
      onError: () => {
        setShowToast(true)
      }
    })
  }

  return (
    <>
      <MainTopBar
        hasBackButton={true}
        handleClickBackButton={() => navigate(-1)}
        leftSide={
          <>
            <div className='flex items-center'>
              <h1 className='text-header-title font-semibold text-text-header'>상담 리포트 </h1>
            </div>
          </>
        }
        rightSide={
          <button
            onClick={() => setShowDeleteModal(true)}
            className='p-2 text-amber-700 hover:text-amber-900 bg-white/20 rounded-full transition-colors'
          >
            <Trash2 size={18} />
          </button>
        }
      />
      {reportData ? (
        <div className='min-h-screen '>
          {/* 메인 콘텐츠 */}
          <div className='px-3 pb-32 space-y-4 pt-3'>
            {/* 리포트 제목 및 기본 정보 카드 */}
            <CardOne>
              <div className='text-center'>
                <h2 className='text-xl font-bold text-gray-800 mb-2'>{reportData.title}</h2>
                <p className='text-gray-600 text-sm mb-4'>{reportData.report_date}</p>
                <p className='text-gray-700 leading-relaxed mb-4 text-sm'>{reportData.step_name}</p>
              </div>
            </CardOne>

            {/* 두 컬럼 섹션 */}
            <div className='grid grid-cols-2 gap-3'>
              {/* 특별한 시간 */}
              {/* <ReportDetailGridBox
                title={reportData.sections[0].title}
                contentText={reportData.sections[0].content}
              /> */}

              {reportData.sections &&
                reportData.sections.map((section, index) => {
                  return (
                    section.title !== '마음에 담고 싶은 이야기' && (
                      <ReportDetailGridBox
                        key={index}
                        title={section.title}
                        contentText={section.content}
                        isList={section.title === '고민의 핵심'}
                      />
                    )
                  )
                })}
            </div>

            {/* 마음에 담고 싶은 이야기 카드 - 전체 너비 */}

            {reportData.sections &&
              reportData.sections.map((section, index) => {
                return (
                  section.title === '마음에 담고 싶은 이야기' && (
                    <ReportDetailGridBox
                      key={`truth-${index}`}
                      title={section.title}
                      content={
                        reportData.impressive_speech ? (
                          <div className='bg-amber-50 rounded-xl p-3 mb-3 border-l-4 border-amber-400'>
                            <p className='text-gray-700 italic leading-relaxed text-sm mb-1 text-center'>
                              "{reportData.impressive_speech}"
                            </p>
                            <p className='text-gray-500 text-[12px] text-right mt-1'>
                              - {`${userInfo.nickname}의 진실 어린 이야기`}
                            </p>
                          </div>
                        ) : undefined
                      }
                      contentText={section.content}
                    />
                  )
                )
              })}
          </div>

          {/* 삭제 확인 모달 */}
          {showDeleteModal && (
            <CommonModal
              onClose={() => setShowDeleteModal(false)}
              title='이 리포트를 삭제하시겠습니까?'
              topButtonText='삭제'
              bottomButtonText='취소'
              topButtonOnClick={handleDelete}
              bottomButtonOnClick={() => setShowDeleteModal(false)}
              isTopButtonDisabled={false}
              hasCloseButton={false}
            />
          )}
        </div>
      ) : (
        <div className='min-h-screen bg-gradient-to-r from-amber-400 to-yellow-300 flex items-center justify-center'>
          <div className='bg-white rounded-2xl p-8 shadow-lg text-center mx-3 mb-16'>
            <h2 className='text-xl font-bold text-gray-800 mb-4'>
              앗! <br /> 리포트를 생성하지 못했어요.
            </h2>
            <p className='text-gray-600 text-sm mb-4'>상담 내용이 충분하지 않아 리포트를 생성하지 못했어요.</p>
          </div>
        </div>
      )}

      {showToast && (
        <ToastMessage message='리포트 삭제에 실패했습니다.' isVisible={showToast} onClose={() => setShowToast(false)} />
      )}
    </>
  )
}
